if (!customElements.get('mobile-product-gallery')) {
  customElements.define(
    'mobile-product-gallery',
    class MobileProductGallery extends HTMLElement {
      constructor() {
        super();

        this.mobileQuery = window.matchMedia('(max-width: 749.98px)');
        this.pointers = new Map();
        this.currentIndex = 0;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.didPinch = false;
        this.suppressSlideClick = false;
      }

      connectedCallback() {
        this.track = this.querySelector('.hm-mobile-gallery__track');
        this.slides = Array.from(this.querySelectorAll('[data-mobile-gallery-slide]'));
        this.dialog = this.querySelector('.hm-mobile-gallery__dialog');
        this.stage = this.querySelector('[data-mobile-gallery-stage]');
        this.fullscreenImage = this.querySelector('[data-mobile-gallery-image]');
        this.count = this.querySelector('[data-mobile-gallery-count]');
        this.previousButton = this.querySelector('[data-mobile-gallery-previous]');
        this.nextButton = this.querySelector('[data-mobile-gallery-next]');
        this.closeButton = this.querySelector('[data-mobile-gallery-close]');
        this.productInfo = this.closest('product-info');
        this.abortController = new AbortController();
        this.signal = this.abortController.signal;

        if (!this.track || !this.dialog || !this.stage || !this.fullscreenImage || this.slides.length === 0) return;

        this.bindEvents();
        this.bindVariantChanges();
        this.handleBreakpointChange();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unsubscribeVariantChange?.();

        if (this.mobileQuery.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.handleBreakpointChangeBound);
        } else {
          this.mobileQuery.removeListener(this.handleBreakpointChangeBound);
        }

        this.unlockPage();
      }

      bindEvents() {
        this.handleBreakpointChangeBound = this.handleBreakpointChange.bind(this);

        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', this.handleBreakpointChangeBound);
        } else {
          this.mobileQuery.addListener(this.handleBreakpointChangeBound);
        }

        this.track.addEventListener('pointerdown', (event) => {
          this.trackPointerStartX = event.clientX;
          this.trackPointerStartY = event.clientY;
          this.suppressSlideClick = false;
        }, { signal: this.signal });

        this.track.addEventListener('pointermove', (event) => {
          if (this.trackPointerStartX === undefined) return;

          const distanceX = Math.abs(event.clientX - this.trackPointerStartX);
          const distanceY = Math.abs(event.clientY - this.trackPointerStartY);
          if (distanceX > 8 || distanceY > 8) this.suppressSlideClick = true;
        }, { signal: this.signal });

        this.track.addEventListener('pointerup', () => {
          this.trackPointerStartX = undefined;
          this.trackPointerStartY = undefined;
        }, { signal: this.signal });

        this.track.addEventListener('pointercancel', () => {
          this.suppressSlideClick = true;
          this.trackPointerStartX = undefined;
          this.trackPointerStartY = undefined;
        }, { signal: this.signal });

        this.track.addEventListener('click', (event) => {
          const slide = event.target.closest('[data-mobile-gallery-slide]');
          if (!slide || !this.contains(slide)) return;

          if (this.suppressSlideClick) {
            event.preventDefault();
            this.suppressSlideClick = false;
            return;
          }

          this.open(Number(slide.dataset.index), slide);
        }, { signal: this.signal });

        this.productInfo?.addEventListener('click', (event) => {
          const stickyMedia = event.target.closest('.sticky-cta__media');
          if (!stickyMedia || !stickyMedia.closest('.sticky-cta') || !this.mobileQuery.matches) return;

          this.open(0, stickyMedia);
        }, { signal: this.signal });

        this.productInfo?.addEventListener('keydown', (event) => {
          const stickyMedia = event.target.closest('.sticky-cta__media');
          if (!stickyMedia || !stickyMedia.closest('.sticky-cta')) return;
          if (!this.mobileQuery.matches || (event.key !== 'Enter' && event.key !== ' ')) return;

          event.preventDefault();
          this.open(0, stickyMedia);
        }, { signal: this.signal });

        this.closeButton?.addEventListener('click', () => this.dialog.close(), { signal: this.signal });
        this.previousButton?.addEventListener('click', () => this.showImage(this.currentIndex - 1), { signal: this.signal });
        this.nextButton?.addEventListener('click', () => this.showImage(this.currentIndex + 1), { signal: this.signal });

        this.dialog.addEventListener('close', () => {
          this.resetZoom();
          this.unlockPage();
          this.opener?.focus({ preventScroll: true });
        }, { signal: this.signal });

        this.stage.addEventListener('pointerdown', this.handleStagePointerDown.bind(this), { signal: this.signal });
        this.stage.addEventListener('pointermove', this.handleStagePointerMove.bind(this), { signal: this.signal });
        this.stage.addEventListener('pointerup', this.handleStagePointerEnd.bind(this), { signal: this.signal });
        this.stage.addEventListener('pointercancel', this.handleStagePointerEnd.bind(this), { signal: this.signal });
      }

      bindVariantChanges() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;
            this.selectMedia(data?.variant?.featured_media?.id);
            this.updateStickyMediaAccessibility(this.mobileQuery.matches);
          });
        }

        document.addEventListener('variant:change', (event) => {
          const detail = event.detail || {};
          const sectionId = detail.sectionId || detail.section_id;
          if (sectionId && String(sectionId) !== String(this.dataset.sectionId)) return;
          this.selectMedia(detail.variant?.featured_media?.id || detail.featured_media?.id);
          this.updateStickyMediaAccessibility(this.mobileQuery.matches);
        }, { signal: this.signal });
      }

      handleBreakpointChange() {
        if (this.mobileQuery.matches) {
          this.hidden = false;
          this.productInfo?.classList.add('hm-mobile-gallery-ready');
          this.updateStickyMediaAccessibility(true);
          return;
        }

        if (this.dialog?.open) this.dialog.close();
        this.hidden = true;
        this.productInfo?.classList.remove('hm-mobile-gallery-ready');
        this.updateStickyMediaAccessibility(false);
      }

      updateStickyMediaAccessibility(isEnabled) {
        const stickyMedia = this.productInfo?.querySelector('.sticky-cta__media');
        if (!stickyMedia) return;

        if (isEnabled) {
          stickyMedia.setAttribute('role', 'button');
          stickyMedia.setAttribute('tabindex', '0');
          stickyMedia.setAttribute('aria-label', 'Open de eerste productfoto op volledig scherm');
          return;
        }

        stickyMedia.removeAttribute('role');
        stickyMedia.removeAttribute('tabindex');
        stickyMedia.removeAttribute('aria-label');
      }

      selectMedia(mediaId) {
        if (!mediaId) return;

        const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
        if (index < 0) return;

        this.currentIndex = index;
        this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'smooth' });
        if (this.dialog.open) this.showImage(index);
      }

      open(index, opener = this.slides[index]) {
        if (!this.mobileQuery.matches) return;

        this.opener = opener;
        this.showImage(index);
        this.lockPage();

        if (!this.dialog.open) this.dialog.showModal();
        this.closeButton?.focus({ preventScroll: true });
      }

      showImage(index) {
        if (index < 0 || index >= this.slides.length) return;

        const slide = this.slides[index];
        this.currentIndex = index;
        this.resetZoom();
        this.fullscreenImage.src = slide.dataset.fullSrc;
        this.fullscreenImage.alt = slide.dataset.alt || '';
        this.count.textContent = `Foto ${index + 1} van ${this.slides.length}`;

        if (this.previousButton) this.previousButton.disabled = index === 0;
        if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
      }

      handleStagePointerDown(event) {
        event.preventDefault();
        this.stage.setPointerCapture?.(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 1) {
          this.singlePointerStart = {
            x: event.clientX,
            y: event.clientY,
            translateX: this.translateX,
            translateY: this.translateY,
          };
          this.didPinch = false;
        } else if (this.pointers.size === 2) {
          const [first, second] = Array.from(this.pointers.values());
          this.pinchStartDistance = this.distance(first, second);
          this.pinchStartScale = this.scale;
          this.didPinch = true;
        }
      }

      handleStagePointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return;

        event.preventDefault();
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2) {
          const [first, second] = Array.from(this.pointers.values());
          const nextDistance = this.distance(first, second);
          const nextScale = this.pinchStartScale * (nextDistance / Math.max(this.pinchStartDistance, 1));

          this.scale = this.clamp(nextScale, 1, 4);
          if (this.scale === 1) {
            this.translateX = 0;
            this.translateY = 0;
          } else {
            this.clampTranslation();
          }
          this.applyTransform();
          return;
        }

        if (this.pointers.size === 1 && this.scale > 1 && this.singlePointerStart) {
          this.translateX = this.singlePointerStart.translateX + event.clientX - this.singlePointerStart.x;
          this.translateY = this.singlePointerStart.translateY + event.clientY - this.singlePointerStart.y;
          this.clampTranslation();
          this.applyTransform();
        }
      }

      handleStagePointerEnd(event) {
        if (!this.pointers.has(event.pointerId)) return;

        const endedPointer = this.pointers.get(event.pointerId);
        this.pointers.delete(event.pointerId);
        this.stage.releasePointerCapture?.(event.pointerId);

        if (this.pointers.size === 1) {
          const remainingPointer = Array.from(this.pointers.values())[0];
          this.singlePointerStart = {
            x: remainingPointer.x,
            y: remainingPointer.y,
            translateX: this.translateX,
            translateY: this.translateY,
          };
          return;
        }

        if (this.pointers.size > 0) return;

        if (this.scale <= 1.03) {
          const distanceX = endedPointer.x - (this.singlePointerStart?.x ?? endedPointer.x);
          const distanceY = endedPointer.y - (this.singlePointerStart?.y ?? endedPointer.y);
          this.resetZoom();

          if (!this.didPinch && Math.abs(distanceX) >= 50 && Math.abs(distanceX) > Math.abs(distanceY) * 1.2) {
            this.showImage(this.currentIndex + (distanceX < 0 ? 1 : -1));
          }
        } else {
          this.clampTranslation();
          this.applyTransform();
        }

        this.singlePointerStart = null;
        this.didPinch = false;
      }

      resetZoom() {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.pointers.clear();
        this.fullscreenImage?.classList.remove('is-zoomed');
        this.applyTransform();
      }

      clampTranslation() {
        const imageWidth = this.fullscreenImage.clientWidth || this.stage.clientWidth;
        const imageHeight = this.fullscreenImage.clientHeight || this.stage.clientHeight;
        const maxX = Math.max(0, (imageWidth * this.scale - this.stage.clientWidth) / 2);
        const maxY = Math.max(0, (imageHeight * this.scale - this.stage.clientHeight) / 2);

        this.translateX = this.clamp(this.translateX, -maxX, maxX);
        this.translateY = this.clamp(this.translateY, -maxY, maxY);
      }

      applyTransform() {
        if (!this.fullscreenImage) return;

        this.fullscreenImage.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
        this.fullscreenImage.classList.toggle('is-zoomed', this.scale > 1);
      }

      distance(first, second) {
        return Math.hypot(second.x - first.x, second.y - first.y);
      }

      clamp(value, minimum, maximum) {
        return Math.min(Math.max(value, minimum), maximum);
      }

      lockPage() {
        document.documentElement.classList.add('hm-mobile-gallery-open');
      }

      unlockPage() {
        document.documentElement.classList.remove('hm-mobile-gallery-open');
      }
    }
  );
}
